/**
 * Starts — at a specified time, or at the start of the day (when the
 * window opens). The line under the choice says what it means in times,
 * Danish echo included. "After the one before" left the choices (user,
 * 15/10/2026); a container already on it still shows it, so it can be
 * switched to one of the two.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Play } from 'lucide-react';

import { TimeIntervalPicker } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { hhmmToMinutes, minutesToHHMM } from '../../../../utils/format';
import { ContainerChip } from '../../../shared/ContainerChip';
import { CountryEcho } from '../../../shared/time/CountryEcho';
import { ClockPair } from '../../../shared/time/ClockPair';
import { useSectionSources } from '../../master/section-sources';
import type { ContainerEditor } from '../../state/useContainerEditor';
import type { PreviousContainer } from '../hooks/use-general-times';
import { MINIMAL_BOX } from '../controls/field-style';
import { NumberStepper } from '../controls/NumberStepper';
import { StepRow, ValueLine } from '../controls/StepRow';
import { ChoiceChips } from '../controls/ChoiceChips';

const OPTIONS = [
  { value: 'FIXED_TIME', label: 'Specified time' },
  { value: 'WINDOW_OPEN', label: 'Start of day' },
] as const;
const AFTER_PREVIOUS = { value: 'AFTER_PREVIOUS', label: 'After the one before' } as const;
type StartMode = (typeof OPTIONS)[number]['value'] | typeof AFTER_PREVIOUS.value;

/** The two choices — plus "After the one before" only where it is still in use. */
const optionsFor = (mode: string) => (mode === AFTER_PREVIOUS.value ? [...OPTIONS, AFTER_PREVIOUS] : OPTIONS);

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  startMin: number | null;
  previous: PreviousContainer | null;
}

export const StartRow: FC<Props> = ({ editor, plan, startMin, previous }) => {
  const { draft, setField } = editor;
  const group = useSectionSources().masterRef?.name ?? 'its campaign group';
  return (
    <StepRow icon={Play} label="Starts" hint="Specified time = the first drop at that time on the Danish clock (🇩🇰), every day all year — the country's time beside it moves when either country changes its clocks. Start of day = the first drop when the country's window opens.">
      <ChoiceChips options={optionsFor(draft.startMode)} value={draft.startMode as StartMode} onChange={(v) => setField('startMode', v)} ariaLabel="When it starts" />
      <ValueLine key={draft.startMode}>
        {draft.startMode === 'FIXED_TIME' ? (
          <>
            <span aria-hidden title="Danish time">🇩🇰</span>
            <TimeIntervalPicker
              boxClassName={MINIMAL_BOX}
              value={draft.startAtMin !== null ? minutesToHHMM(draft.startAtMin) : '12:00'}
              onChange={(v) => setField('startAtMin', hhmmToMinutes(v))}
            />
            <CountryEcho min={draft.startAtMin !== null ? startMin : null} className="text-xs" />
          </>
        ) : null}
        {draft.startMode === 'AFTER_PREVIOUS' && previous ? (
          <>
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <ContainerChip name={previous.name} color={previous.color} />
              ends <ClockPair min={previous.lastMin} strong /> · then wait
            </span>
            <NumberStepper value={draft.gapMin} onChange={(v) => setField('gapMin', v)} min={0} max={1439} step={5} ariaLabel="Wait after the one before" />
            <FirstDrop startMin={startMin} />
          </>
        ) : null}
        {draft.startMode === 'AFTER_PREVIOUS' && !previous ? (
          <>
            <span className="text-xs text-muted-foreground">
              Nothing runs before it in {group} — it starts when the window opens:
            </span>
            <FirstDrop startMin={startMin} />
          </>
        ) : null}
        {draft.startMode === 'WINDOW_OPEN' ? (
          <span className="inline-flex items-center gap-3 text-xs text-muted-foreground">
            the window opens at <ClockPair min={plan.windowStartMin} strong />
          </span>
        ) : null}
      </ValueLine>
    </StepRow>
  );
};

const FirstDrop: FC<{ startMin: number | null }> = ({ startMin }) =>
  startMin === null ? null : (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
      → starts <ClockPair min={startMin} strong />
    </span>
  );
